import { PropsWithChildren, useContext, useState } from 'react';
import { OverlayTriggerStateContext } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import Button from '~/components/buttons/NewButton';
import ModalActions from './ModalActions';

interface Props {
  action?: 'confirm' | 'destructive';
  cancelLabel?: string;
  confirmLabel: string;
  confirmIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  isConfirmDisabled?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => boolean;
  className?: string;
  confirmDataTestId?: string;
  cancelDataTestId?: string;
  isExternalLoading?: boolean;
}

const SimpleModalActions: React.FC<PropsWithChildren<Props>> = (props) => {
  const { t } = useTranslation('inputs', { keyPrefix: 'modal' });
  const triggerContext = useContext(OverlayTriggerStateContext);
  const [isConfirming, setIsConfirming] = useState(false);

  const handleClose = () => {
    if (props.onCancel && !props.onCancel()) return;

    if (triggerContext) {
      triggerContext.close();
    }
    if (props.onOpenChange) {
      props.onOpenChange(false);
    }
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await props.onConfirm();
    } finally {
      setIsConfirming(false);
    }
  };

  const isLoading = isConfirming || props.isExternalLoading;

  return (
    <ModalActions className={props.className}>
      <Button disabled={isLoading} onClick={handleClose} variant="outlined" data-testid={props.cancelDataTestId}>
        {props.cancelLabel ?? t('cancel')}
      </Button>
      <Button
        action={props.action}
        disabled={props.isConfirmDisabled}
        loading={isLoading}
        onClick={handleConfirm}
        data-testid={props.confirmDataTestId}
        Icon={props.confirmIcon}
      >
        {props.confirmLabel}
      </Button>
    </ModalActions>
  );
};

export default SimpleModalActions;
